import React from 'react';
import { Tag, Tooltip } from 'antd';
import type { IdentityType } from '@/types/user';

interface IdentityBadgeProps {
  identities: IdentityType[];
  size?: 'small' | 'default';
  isPermanentVip?: boolean;
}

// 身份徽章配置 - 与徽章说明弹窗中的描述完全一致
const IDENTITY_CONFIG: Record<Exclude<IdentityType, 'regular'>, { label: string; color: string; bgColor: string; borderColor: string; svgName: string; description: string }> = {
  coach: {
    label: '产品教练',
    color: 'purple',
    bgColor: '#f9f0ff',
    borderColor: '#d3adf7',
    svgName: 'identity-coach',
    description: '学识 - 博士帽代表持续学习和知识分享，是平台的智囊团',
  },
  super_pm: {
    label: '超级产品经理',
    color: 'red',
    bgColor: '#fff1f0',
    borderColor: '#ffa39e',
    svgName: 'identity-super-pm',
    description: '尊贵 - 最珍贵的钻石，代表最高级别的用户身份',
  },
  angel_pm: {
    label: '天使产品',
    color: '#ff6b9d',
    bgColor: '#fff0f5',
    borderColor: '#ffadd2',
    svgName: 'identity-angel-pm',
    description: '新生 - 通过邀请码加入的天使用户，拥有无限成长潜力',
  },
};

// SVG 图标组件 - 使用 public/badges 目录下的 SVG 文件
const IdentityIcon: React.FC<{ svgName: string; size?: number }> = ({ svgName, size = 24 }) => {
  const svgPath = `/badges/${svgName}.svg`;
  return (
    <img
      src={svgPath}
      alt={svgName}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
};

const IdentityBadge: React.FC<IdentityBadgeProps> = ({ identities, size = 'small', isPermanentVip }) => {
  const fontSize = size === 'small' ? 12 : 14;
  const iconSize = size === 'small' ? 20 : 24;

  // 过滤掉 regular 身份，只展示特殊身份
  const specialIdentities: Exclude<IdentityType, 'regular'>[] = identities?.filter((id): id is Exclude<IdentityType, 'regular'> => id !== 'regular') || [];

  // 确定要显示的身份标签
  const displayIdentities: Exclude<IdentityType, 'regular'>[] = [...specialIdentities];

  // 如果有 coach 身份，根据 isPermanentVip 决定显示什么
  if (specialIdentities.includes('coach')) {
    // 移除 coach，替换为对应的身份
    const index = displayIdentities.indexOf('coach');
    if (index > -1) {
      displayIdentities.splice(index, 1);
    }
    // 永久会员显示 super_pm，否则显示 coach
    displayIdentities.push(isPermanentVip ? 'super_pm' : 'coach');
  }

  // 如果没有特殊身份，返回 null（不展示任何标签）
  if (displayIdentities.length === 0) {
    return null;
  }

  return (
    <>
      {displayIdentities.map((identity) => {
        const config = IDENTITY_CONFIG[identity];
        if (!config) return null;

        return (
          <Tooltip key={identity} title={config.description}>
            <Tag
              style={{
                fontSize,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                cursor: 'pointer',
                backgroundColor: config.bgColor,
                borderColor: config.borderColor,
                color: config.color,
              }}
            >
              <IdentityIcon svgName={config.svgName} size={iconSize} />
              {config.label}
            </Tag>
          </Tooltip>
        );
      })}
    </>
  );
};

export default IdentityBadge;
